"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ImageScale } from "@/helpers/image.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropContentPresentation } from "../dropContentPresentation";
import type { DropIdentityMode, DropInteractionParams, DropLocation, DropTimestampLayout } from "../drop.types";
import { hasDropFooter } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly inlineAuthorOnDesktop?: boolean | undefined;
  readonly mediaImageScale?: ImageScale | undefined;
  readonly fullWidthMedia?: boolean | undefined;
  readonly fullWidthLinkPreviews?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(107);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    footer,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    inlineAuthorOnDesktop: t4,
    mediaImageScale,
    fullWidthMedia: t5,
    fullWidthLinkPreviews: t6,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t7,
    isVotingControlsLocked: t8,
    contentPresentation: t9,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const inlineAuthorOnDesktop = t4 === undefined ? false : t4;
  const fullWidthMedia = t5 === undefined ? false : t5;
  const fullWidthLinkPreviews = t6 === undefined ? false : t6;
  const isVotingClosed = t7 === undefined ? false : t7;
  const isVotingControlsLocked = t8 === undefined ? false : t8;
  const contentPresentation = t9 === undefined ? "default" : t9;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let identityProfile;
  let t10;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t11;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t11 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t11;
    } else {
      t11 = $[8];
    }
    visibleMetadata = t11;
    t10 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t10;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t10 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t10;
  const showIdentity = identityMode !== "hidden";
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const {
    isOpen: isVoteModalOpen,
    open: openVoteModal,
    close: closeVoteModal
  } = useVotingModalState(isVotingActionLocked);
  let t11;
  if ($[9] !== hasTouch || $[10] !== showInteractions) {
    t11 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[9] = hasTouch;
    $[10] = showInteractions;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const handleLongPress = t11;
  let t12;
  if ($[12] !== activePartIndex || $[13] !== drop || $[14] !== onReply) {
    t12 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[12] = activePartIndex;
    $[13] = drop;
    $[14] = onReply;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handleOnReply = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsSlideUp(false);
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const handleOnAddReaction = t13;
  let t14;
  if ($[17] !== openVoteModal) {
    t14 = () => {
      openVoteModal();
    };
    $[17] = openVoteModal;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const handleVoteButtonClick = t14;
  let t15;
  if ($[19] !== canShowVote || $[20] !== drop || $[21] !== handleVoteButtonClick || $[22] !== isVotingActionLocked || $[23] !== showInteractions) {
    t15 = canShowVote && showInteractions && !isVotingActionLocked ? <VotingModalButton drop={drop} onClick={handleVoteButtonClick} /> : null;
    $[19] = canShowVote;
    $[20] = drop;
    $[21] = handleVoteButtonClick;
    $[22] = isVotingActionLocked;
    $[23] = showInteractions;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const voteAction = t15;
  let t16;
  if ($[25] !== activePartIndex || $[26] !== contentPresentation || $[27] !== drop || $[28] !== embedDepth || $[29] !== embedPath || $[30] !== fullWidthLinkPreviews || $[31] !== fullWidthMedia || $[32] !== handleLongPress || $[33] !== maxEmbedDepth || $[34] !== mediaImageScale || $[35] !== onDropContentClick || $[36] !== onQuoteClick || $[37] !== quotePath) {
    t16 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} mediaImageScale={mediaImageScale} fullWidthMedia={fullWidthMedia} fullWidthLinkPreviews={fullWidthLinkPreviews} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[25] = activePartIndex;
    $[26] = contentPresentation;
    $[27] = drop;
    $[28] = embedDepth;
    $[29] = embedPath;
    $[30] = fullWidthLinkPreviews;
    $[31] = fullWidthMedia;
    $[32] = handleLongPress;
    $[33] = maxEmbedDepth;
    $[34] = mediaImageScale;
    $[35] = onDropContentClick;
    $[36] = onQuoteClick;
    $[37] = quotePath;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const content = t16;
  const shouldOffsetRows = showIdentity && !inlineAuthorOnDesktop;
  const t17 = !(typeof winningThreshold === "number" && winningThreshold > 0);
  let t18;
  if ($[39] !== activePartIndex || $[40] !== drop || $[41] !== handleOnReply || $[42] !== isMobile || $[43] !== showInteractions || $[44] !== showReplyAndQuote) {
    t18 = !isMobile && showInteractions && showReplyAndQuote ? <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} /> : null;
    $[39] = activePartIndex;
    $[40] = drop;
    $[41] = handleOnReply;
    $[42] = isMobile;
    $[43] = showInteractions;
    $[44] = showReplyAndQuote;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = `tw-relative tw-z-10 tw-flex tw-w-full tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left ${inlineAuthorOnDesktop ? "tw-flex-col tw-gap-y-2" : "tw-gap-x-3"}`;
  let t20;
  if ($[46] !== content || $[47] !== drop || $[48] !== identityMode || $[49] !== inlineAuthorOnDesktop || $[50] !== showIdentity || $[51] !== showWaveInfo || $[52] !== timestampLayout || $[53] !== winningThreshold) {
    t20 = inlineAuthorOnDesktop ? <>{showIdentity && <div className="tw-flex tw-w-full tw-items-center tw-gap-x-2"><WaveDropAuthorPfp drop={drop} /><div className="tw-min-w-0 tw-flex-1">{identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} winningThreshold={winningThreshold} timestampLayout={timestampLayout} />}</div></div>}{content}</> : <>{showIdentity && <WaveDropAuthorPfp drop={drop} />}<div className="tw-flex tw-w-full tw-flex-col">{showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} winningThreshold={winningThreshold} timestampLayout={timestampLayout} />)}{content}</div></>;
    $[46] = content;
    $[47] = drop;
    $[48] = identityMode;
    $[49] = inlineAuthorOnDesktop;
    $[50] = showIdentity;
    $[51] = showWaveInfo;
    $[52] = timestampLayout;
    $[53] = winningThreshold;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t19 || $[56] !== t20) {
    t21 = <div className={t19}>{t20}</div>;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== drop.id || $[59] !== identityProfile || $[60] !== isSelfNominee || $[61] !== shouldOffsetRows) {
    t22 = identityProfile && <div className={`${shouldOffsetRows ? "tw-ml-[3.25rem]" : ""} tw-px-4`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[58] = drop.id;
    $[59] = identityProfile;
    $[60] = isSelfNominee;
    $[61] = shouldOffsetRows;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== drop.id || $[64] !== visibleMetadata) {
    t23 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[63] = drop.id;
    $[64] = visibleMetadata;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== drop || $[67] !== footer || $[68] !== isVotingClosed || $[69] !== isVotingControlsLocked || $[70] !== showInteractions || $[71] !== voteAction || $[72] !== winningThreshold || $[73] !== winningThresholdMinDurationMs) {
    t24 = (showInteractions || !hasDropFooter(footer)) && <ParticipationDropFooter drop={drop} voteAction={voteAction} showInteractions={showInteractions} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[66] = drop;
    $[67] = footer;
    $[68] = isVotingClosed;
    $[69] = isVotingControlsLocked;
    $[70] = showInteractions;
    $[71] = voteAction;
    $[72] = winningThreshold;
    $[73] = winningThresholdMinDurationMs;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== footer || $[76] !== shouldOffsetRows) {
    t25 = hasDropFooter(footer) && <div className={`${shouldOffsetRows ? "tw-ml-[3.25rem]" : ""} tw-px-4 tw-pb-4 tw-pt-2`}>{footer}</div>;
    $[75] = footer;
    $[76] = shouldOffsetRows;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== t22 || $[79] !== t23 || $[80] !== t24 || $[81] !== t25) {
    t26 = <div className="tw-flex tw-w-full tw-flex-col">{t22}{t23}{t24}{t25}</div>;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== closeVoteModal || $[84] !== drop || $[85] !== isMobileScreen || $[86] !== isVoteModalOpen || $[87] !== showInteractions) {
    t27 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} /> : <VotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} />);
    $[83] = closeVoteModal;
    $[84] = drop;
    $[85] = isMobileScreen;
    $[86] = isVoteModalOpen;
    $[87] = showInteractions;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== drop || $[90] !== handleOnReply || $[91] !== isSlideUp || $[92] !== isVotingActionLocked || $[93] !== longPressTriggered || $[94] !== showInteractions || $[95] !== showReplyAndQuote) {
    t28 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} showVoting={!isVotingActionLocked} />;
    $[89] = drop;
    $[90] = handleOnReply;
    $[91] = isSlideUp;
    $[92] = isVotingActionLocked;
    $[93] = longPressTriggered;
    $[94] = showInteractions;
    $[95] = showReplyAndQuote;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== drop || $[98] !== isActiveDrop || $[99] !== location || $[100] !== t17 || $[101] !== t18 || $[102] !== t21 || $[103] !== t26 || $[104] !== t27 || $[105] !== t28) {
    t29 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location} useRankStyles={t17} floatingActions={t18}>{t21}{t26}{t27}{t28}</ParticipationDropContainer>;
    $[97] = drop;
    $[98] = isActiveDrop;
    $[99] = location;
    $[100] = t17;
    $[101] = t18;
    $[102] = t21;
    $[103] = t26;
    $[104] = t27;
    $[105] = t28;
    $[106] = t29;
  } else {
    t29 = $[106];
  }
  return t29;
}